import { useLocaleStore } from '@/i18n';
import { useAuthStore, useSessionKickedStore } from '@/store/auth';
import { getCachedDeviceModel, getDeviceUUID, getNetworkType } from '@/lib/deviceInfo';

// Default to the production API host so a build without VITE_API_BASE_URL
// still talks to the real backend instead of falling through to the SPA
// HTML fallback and surfacing a "VITE_API_BASE_URL is not set" error on the
// home page. The env var still wins when set (e.g. staging, local proxy).
const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? 'https://api.tt6.com').replace(/\/$/, '');

export class HttpError extends Error {
  constructor(public status: number, message: string, public code?: number) {
    super(message);
  }
}

function resolveUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  if (API_BASE_URL && url.startsWith('/api/v1/')) return `${API_BASE_URL}${url}`;
  return url;
}

type Envelope<T> = {
  code: number;
  message?: string;
  action?: string;
  data?: T;
  error_payload?: { country?: string; ip?: string; login_time?: string };
};

const isV1Path = (url: string) => url.startsWith('/api/v1/');

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const locale = useLocaleStore.getState().locale;
  const token = useAuthStore.getState().token;
  const headers = new Headers(init?.headers);
  if (!headers.has('Accept-Language')) headers.set('Accept-Language', locale);
  if (token && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
  const deviceUUID = getDeviceUUID();
  if (deviceUUID && !headers.has('X-DEVICE-UUID')) headers.set('X-DEVICE-UUID', deviceUUID);
  if (!headers.has('X-Client-Network')) headers.set('X-Client-Network', getNetworkType());
  const deviceModel = getCachedDeviceModel();
  if (deviceModel && !headers.has('X-Device-Model')) headers.set('X-Device-Model', deviceModel);

  const resolvedUrl = resolveUrl(url);
  const res = await fetch(resolvedUrl, { ...init, headers });
  const method = init?.method ?? 'GET';

  if (!res.ok) {
    let message = `${method} ${url} failed: ${res.status}`;
    let code: number | undefined;
    try {
      const body = (await res.clone().json()) as { code?: number; message?: string };
      if (body?.message) message = body.message;
      if (typeof body?.code === 'number') code = body.code;
    } catch {
      // non-JSON error body — keep default message
    }
    // Session expired / unauthenticated. Clear auth so pages branching on
    // `isAuthed` (Mine, Home, etc.) flip to their logged-out state instead of
    // staying stuck on a loading spinner waiting for data they'll never get.
    if (res.status === 401) {
      useAuthStore.getState().clear();
    }
    throw new HttpError(res.status, message, code);
  }

  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.toLowerCase().includes('application/json')) {
    const bodyStart = (await res.text()).trim().slice(0, 80);
    const hint =
      isV1Path(url) && !API_BASE_URL
        ? 'VITE_API_BASE_URL is not set, so the app is likely receiving the Vite HTML fallback instead of the API response.'
        : `Expected JSON from ${method} ${resolvedUrl}.`;
    throw new HttpError(res.status, `${hint} Response started with: ${bodyStart || '<empty>'}`);
  }

  const body = await res.json();

  if (isV1Path(url) && body && typeof body === 'object' && 'code' in body) {
    const env = body as Envelope<T>;
    if (env.code !== 0) {
      if (env.code === 4021) {
        const p = env.error_payload;
        useSessionKickedStore.getState().setKicked({
          country: p?.country ?? '',
          ip: p?.ip ?? '',
          login_time: p?.login_time ?? '',
        });
        // Auth is cleared only after the user dismisses the popup, so they
        // stay on the current page and see the session-kicked details first.
      }
      throw new HttpError(res.status, env.message ?? `${method} ${url} failed`, env.code);
    }
    return env.data as T;
  }

  return body as T;
}

export const apiGet = <T>(url: string) => request<T>(url);

/** Raw GET that returns the unwrapped `Response`. Use for endpoints whose
 *  payload isn't JSON — e.g. `/api/v1/user/generate-qr-user-two-fa` returns
 *  the QR as `image/png` body with the TOTP secret in the
 *  Authentication-Secret response header (RenderResponseImage on the
 *  backend). Shares auth / locale / base-URL wiring with `request`. */
export async function apiGetRaw(url: string): Promise<Response> {
  const locale = useLocaleStore.getState().locale;
  const token = useAuthStore.getState().token;
  const headers = new Headers();
  headers.set('Accept-Language', locale);
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const deviceUUID = getDeviceUUID();
  if (deviceUUID && !headers.has('X-DEVICE-UUID')) headers.set('X-DEVICE-UUID', deviceUUID);
  if (!headers.has('X-Client-Network')) headers.set('X-Client-Network', getNetworkType());
  const deviceModel = getCachedDeviceModel();
  if (deviceModel && !headers.has('X-Device-Model')) headers.set('X-Device-Model', deviceModel);

  const res = await fetch(resolveUrl(url), { headers });
  if (!res.ok) {
    if (res.status === 401) useAuthStore.getState().clear();
    throw new HttpError(res.status, `GET ${url} failed: ${res.status}`);
  }
  return res;
}

export const apiPost = <T>(url: string, body: unknown) =>
  request<T>(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

export const apiPut = <T>(url: string, body: unknown) =>
  request<T>(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

export const apiDelete = <T = void>(url: string) =>
  request<T>(url, { method: 'DELETE' });
